import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { driverEarningsAPI, driverIncentiveAPI, adminSettingsAPI } from '../services/api';
import { Wallet, TrendingUp, Calendar, RefreshCw, AlertCircle, BarChart3, Gift, CheckCircle2, Circle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface DriverEarningsData {
  overview: {
    totalEarnings: number;
    totalBookings: number;
    avgEarningsPerBooking: number;
    thisWeekEarnings: number;
    thisWeekBookings: number;
    lastWeekEarnings: number;
    lastWeekBookings: number;
    thisYearEarnings: number;
  };
  weeklyEarnings: Array<{
    week: string;
    earnings: number;
    bookings: number;
  }>;
  recentBookings: Array<{
    bookingId: number;
    date: string;
    amount: number;
    createdAt: string;
  }>;
}

const DriverEarningsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<DriverEarningsData | null>(null);
  const [incentives, setIncentives] = useState<any>(null);
  const [incentiveCheckpoints, setIncentiveCheckpoints] = useState<Array<{ bookings: number; amount: number }>>([]);

  const fetchEarnings = async () => {
    try {
      setError('');
      const [earningsResponse, incentivesResponse, settingsResponse] = await Promise.all([
        driverEarningsAPI.getDriverEarnings(),
        driverIncentiveAPI.getMyIncentives().catch(() => ({ success: false, data: null })),
        adminSettingsAPI.getSettings().catch(() => ({ success: false, data: null }))
      ]);
      
      if (earningsResponse.success) {
        setData(earningsResponse.data);
      } else {
        setError(earningsResponse.message || t('earnings.failedToLoadEarnings'));
      }
      
      if (incentivesResponse.success && incentivesResponse.data) {
        setIncentives(incentivesResponse.data);
      }
      
      if (settingsResponse.success && settingsResponse.data?.incentiveCheckpoints) {
        setIncentiveCheckpoints(settingsResponse.data.incentiveCheckpoints);
      }
    } catch (err: any) {
      console.error('Error fetching earnings:', err);
      setError(err.response?.data?.message || t('earnings.failedToLoadEarnings'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchEarnings();
  }, []);

  const onRefresh = () => {
    setRefreshing(true);
    fetchEarnings();
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toFixed(2)}`;
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 text-black animate-spin mx-auto mb-4" />
                <p className="text-gray-600">{t('earnings.loadingEarnings')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('earnings.myEarnings')}</h1>
            <p className="text-gray-600 mt-1">{t('earnings.trackEarnings')}</p>
          </div>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {t('earnings.refresh')}
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {data && (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-blue-600 rounded-lg shadow-md p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <Wallet className="h-8 w-8" />
                </div>
                <p className="text-blue-100 text-sm mb-1">{t('earnings.totalEarnings')}</p>
                <p className="text-3xl font-bold">{formatCurrency(data.overview.totalEarnings)}</p>
              </div>

              <div className="bg-green-600 rounded-lg shadow-md p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <Calendar className="h-8 w-8" />
                </div>
                <p className="text-green-100 text-sm mb-1">{t('earnings.totalBookings')}</p>
                <p className="text-3xl font-bold">{data.overview.totalBookings}</p>
              </div>

              <div className="bg-purple-600 rounded-lg shadow-md p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <TrendingUp className="h-8 w-8" />
                </div>
                <p className="text-purple-100 text-sm mb-1">{t('earnings.avgPerBooking')}</p>
                <p className="text-3xl font-bold">{formatCurrency(data.overview.avgEarningsPerBooking)}</p>
              </div>
            </div>

            {/* Incentive Journey */}
            {incentives && incentiveCheckpoints.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <div className="flex items-center gap-2 mb-6">
                  <Gift className="h-6 w-6 text-black" />
                  <h2 className="text-xl font-bold text-gray-900">{t('earnings.incentiveJourney')}</h2>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-200">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">{t('earnings.pending')}</p>
                    <p className="text-2xl font-bold text-amber-600">₹{incentives.totalPending.toFixed(2)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">{t('earnings.paid')}</p>
                    <p className="text-2xl font-bold text-green-600">₹{incentives.totalPaid.toFixed(2)}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('earnings.checkpoints')}</h3>
                  <div className="space-y-4">
                    {incentiveCheckpoints
                      .sort((a, b) => a.bookings - b.bookings)
                      .map((checkpoint, index) => {
                        const achieved = incentives.incentives.find(
                          (inc: any) => inc.checkpoint === checkpoint.bookings
                        );
                        const isCompleted = data && data.overview.totalBookings >= checkpoint.bookings;
                        const progress = data 
                          ? Math.min((data.overview.totalBookings / checkpoint.bookings) * 100, 100)
                          : 0;
                        
                        return (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                {isCompleted ? (
                                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                                ) : (
                                  <Circle className="h-6 w-6 text-gray-400" />
                                )}
                                <div>
                                  <p className="font-semibold text-gray-900">{checkpoint.bookings} {t('earnings.bookings')}</p>
                                  <p className="text-sm text-black">₹{checkpoint.amount.toFixed(2)} {t('earnings.reward')}</p>
                                </div>
                              </div>
                              {achieved && (
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  achieved.status === 'paid' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-amber-100 text-amber-800'
                                }`}>
                                  {achieved.status === 'paid' ? t('earnings.paid') : t('earnings.pending')}
                                </span>
                              )}
                            </div>
                            {!isCompleted && data && (
                              <div className="mt-3">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs text-gray-600">{t('earnings.progress')}</span>
                                  <span className="text-xs text-gray-600">
                                    {data.overview.totalBookings} / {checkpoint.bookings} {t('earnings.bookings')}
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-black h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            )}

            {/* Period Comparison */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">{t('earnings.thisWeek')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-gray-600 text-sm mb-1">{t('earnings.earnings')}</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(data.overview.thisWeekEarnings)}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm mb-1">{t('earnings.bookings')}</p>
                  <p className="text-2xl font-bold text-gray-900">{data.overview.thisWeekBookings}</p>
                </div>
                {data.overview.lastWeekEarnings > 0 && (
                  <div className="md:col-span-2 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">
                        {data.overview.thisWeekEarnings >= data.overview.lastWeekEarnings ? '↑' : '↓'} {t('earnings.vsLastWeek')}
                      </span>
                      <span
                        className={`text-lg font-semibold ${
                          data.overview.thisWeekEarnings >= data.overview.lastWeekEarnings
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        {(
                          ((data.overview.thisWeekEarnings - data.overview.lastWeekEarnings) /
                            data.overview.lastWeekEarnings) *
                          100
                        ).toFixed(1)}
                        %
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Weekly Earnings Chart */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex items-center gap-2 mb-6">
                <BarChart3 className="h-6 w-6 text-black" />
                <h2 className="text-xl font-bold text-gray-900">{t('earnings.weeklyEarnings')}</h2>
              </div>
              <div className="flex items-end justify-around gap-2 md:gap-4 h-64 px-2">
                {data.weeklyEarnings.map((week, index) => {
                  const maxEarnings = Math.max(...data.weeklyEarnings.map((w) => w.earnings));
                  const barHeight = maxEarnings > 0 ? (week.earnings / maxEarnings) * 100 : 0;
                  const weekStartDate = week.week.split(' - ')[0];

                  return (
                    <div key={index} className="flex-1 flex flex-col items-center h-full group relative">
                      <div className="w-full flex-1 flex items-end mb-2 relative">
                        <div className="w-full bg-gray-200 rounded-t-lg h-full relative">
                          <div
                            className="w-full bg-black rounded-t-lg transition-all duration-300 hover:bg-gray-800 cursor-pointer"
                            style={{ height: `${barHeight}%`, minHeight: barHeight > 0 ? '4px' : '0' }}
                          >
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-xs font-semibold text-gray-700 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity bg-white px-2 py-1 rounded shadow-md">
                              {formatCurrency(week.earnings)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 text-center mt-2 break-words max-w-full">
                        {weekStartDate}
                      </p>
                      <p className="text-xs font-semibold text-black mt-1">
                        {formatCurrency(week.earnings)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent Bookings */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">{t('earnings.recentBookings')}</h2>
              {data.recentBookings.length > 0 ? (
                <div className="space-y-3">
                  {data.recentBookings.map((booking, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div>
                        <p className="font-semibold text-gray-900">{t('earnings.booking')} #{booking.bookingId}</p>
                        <p className="text-sm text-gray-600">
                          {new Date(booking.date).toLocaleDateString()}
                        </p>
                      </div>
                      <p className="text-lg font-bold text-green-600">{formatCurrency(booking.amount)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-600 py-8">{t('earnings.noRecentBookings')}</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DriverEarningsPage;

